/**
 * @module AdaptersPage
 * @package @geenius/adapters/solidjs
 * @description Renders the Tailwind-based adapters overview for the SolidJS
 * package. The page summarizes adapter health, supports status filtering, and
 * provides an accessible modal workflow for configuring each adapter domain.
 */

import type {
  AdapterDomain,
  AdapterStatusType,
  DomainAdapterConfig,
} from "@geenius/adapters";
import {
  ADAPTER_DOMAINS,
  ADAPTER_STATUSES,
  DOMAIN_LABELS,
  getDomainConfig,
  getProvidersForDomain,
  isAdaptersConfigured,
} from "@geenius/adapters";
import type { JSX } from "solid-js";
import {
  createEffect,
  createSignal,
  createUniqueId,
  ErrorBoundary,
  For,
  onCleanup,
  Show,
} from "solid-js";
import { AdapterConfigForm } from "../components/AdapterConfigForm";
import { AdapterList } from "../components/AdapterList";
import { AdapterStatusBadge } from "../components/AdapterStatusBadge";
import { cn } from "../lib/utils";
import { createAdapters } from "../primitives";
import { PageBoundaryFallback } from "./PageBoundaryFallback";

const DIALOG_FOCUSABLE_SELECTORS =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type RegistryEntry = {
  domain: AdapterDomain;
  provider: string;
  status: AdapterStatusType;
};

/**
 * Props accepted by the SolidJS adapters overview page.
 *
 * @property onNavigateDetail Optional callback used instead of opening the inline modal
 * @property onConfigure Optional callback invoked with submitted adapter configuration
 * @property class Optional class merged onto the page root
 * @property configByDomain Optional configuration snapshot used to hydrate modal forms
 * @property registryEntries Optional adapter registry snapshot rendered above the grid
 */
export interface AdaptersPageProps {
  onNavigateDetail?: (domain: AdapterDomain) => void;
  onConfigure?: (
    domain: AdapterDomain,
    config: DomainAdapterConfig,
  ) => Promise<void> | void;
  class?: string;
  configByDomain?: Partial<Record<AdapterDomain, DomainAdapterConfig>>;
  registryEntries?: RegistryEntry[];
}

/**
 * Returns focusable descendants for an active dialog container.
 *
 * @param container Dialog container used to scope the focus trap
 * @returns Interactive descendants that should participate in tab order
 */
function getDialogFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(DIALOG_FOCUSABLE_SELECTORS),
  );
}

/**
 * Renders the adapter management dashboard for the SolidJS variant.
 *
 * @param props Page props controlling navigation overrides and registry data
 * @returns A dashboard with status filters, adapter cards, and a modal editor
 * @example
 * ```tsx
 * <AdaptersPage onNavigateDetail={(domain) => navigate(`/adapters/${domain}`)} />
 * ```
 */
export function AdaptersPage(props: AdaptersPageProps): JSX.Element {
  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <PageBoundaryFallback
          class={props.class}
          error={error}
          onRetry={reset}
          title="Adapters failed to load"
        />
      )}
    >
      <AdaptersPageContent {...props} />
    </ErrorBoundary>
  );
}

function AdaptersPageContent(props: AdaptersPageProps): JSX.Element {
  const ctx = createAdapters();
  const [selectedDomain, setSelectedDomain] =
    createSignal<AdapterDomain | null>(null);
  const [filterStatus, setFilterStatus] = createSignal<
    AdapterStatusType | undefined
  >();
  const modalTitleId = createUniqueId();
  let modalRef: HTMLDivElement | undefined;
  const registryEntries = () => props.registryEntries ?? [];

  const connected = () =>
    ADAPTER_DOMAINS.filter(
      (domain) => ctx.statuses[domain]?.status === "connected",
    ).length;
  const errors = () =>
    ADAPTER_DOMAINS.filter((domain) => ctx.statuses[domain]?.status === "error")
      .length;
  const initialConfigFor = (domain: AdapterDomain) =>
    props.configByDomain?.[domain] ??
    (isAdaptersConfigured() ? getDomainConfig(domain) : undefined) ??
    undefined;

  createEffect(() => {
    if (!selectedDomain() || !modalRef) {
      return;
    }

    const dialog = modalRef;
    const ownerDocument = dialog.ownerDocument;
    const ownerWindow = ownerDocument.defaultView;
    const HTMLElementCtor = ownerWindow?.HTMLElement;
    const previousActiveElement =
      HTMLElementCtor && ownerDocument.activeElement instanceof HTMLElementCtor
        ? ownerDocument.activeElement
        : null;
    const initialFocusables = getDialogFocusableElements(dialog);
    (initialFocusables[0] ?? dialog).focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        setSelectedDomain(null);
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      // Re-query the focusable list on every Tab so the trap reflects the
      // current dialog content (forms can mount inputs/buttons after the
      // initial focus snapshot, e.g. when provider state hydrates async).
      const currentFocusables = getDialogFocusableElements(dialog);
      const firstFocusableElement = currentFocusables[0];
      const lastFocusableElement = currentFocusables.at(-1);
      const activeElement = ownerDocument.activeElement;

      if (!firstFocusableElement || !lastFocusableElement) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      if (event.shiftKey && activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    ownerDocument.addEventListener("keydown", handleKeyDown);

    onCleanup(() => {
      ownerDocument.removeEventListener("keydown", handleKeyDown);
      previousActiveElement?.focus();
    });
  });

  return (
    <Show
      when={!ctx.isLoading}
      fallback={
        <div class={cn("mx-auto max-w-4xl space-y-6", props.class)}>
          <div
            class="animate-pulse motion-reduce:animate-none space-y-4"
            role="status"
            aria-label="Loading adapters"
          >
            <div class="h-8 w-48 rounded bg-muted" />
            <div class="h-4 w-72 rounded bg-muted/70" />
            <div class="grid gap-3 sm:grid-cols-3">
              <For each={[1, 2, 3]}>
                {() => (
                  <div class="h-20 rounded-lg border border-border bg-card" />
                )}
              </For>
            </div>
            <div class="grid gap-3 sm:grid-cols-2">
              <For each={[1, 2, 3, 4]}>
                {() => (
                  <div class="h-28 rounded-lg border border-border bg-card" />
                )}
              </For>
            </div>
          </div>
        </div>
      }
    >
      <div
        class={cn("mx-auto max-w-4xl space-y-8 text-foreground", props.class)}
      >
        <header class="space-y-1">
          <h1 class="text-2xl font-extrabold text-foreground">Adapters</h1>
          <p class="text-sm text-muted-foreground">
            Manage your infrastructure adapters - database, auth, AI, storage,
            payments, and admin.
          </p>
        </header>

        <section class="grid gap-3 sm:grid-cols-3">
          <div class="rounded-lg border border-border bg-card p-4 shadow-sm">
            <p class="text-xs font-semibold uppercase text-muted-foreground">
              Total
            </p>
            <p class="mt-1 text-2xl font-extrabold text-foreground">
              {ADAPTER_DOMAINS.length}
            </p>
            <p class="text-[0.625rem] text-muted-foreground">adapter domains</p>
          </div>
          <div class="rounded-lg border border-primary/20 bg-primary/10 p-4 shadow-sm">
            <p class="text-xs font-semibold uppercase text-primary">
              Connected
            </p>
            <p class="mt-1 text-2xl font-extrabold text-primary">
              {connected()}
            </p>
            <p class="text-[0.625rem] text-primary/70">active adapters</p>
          </div>
          <Show when={errors() > 0}>
            <div class="rounded-lg border border-destructive/20 bg-destructive/10 p-4 shadow-sm">
              <p class="text-xs font-semibold uppercase text-destructive">
                Errors
              </p>
              <p class="mt-1 text-2xl font-extrabold text-destructive">
                {errors()}
              </p>
              <p class="text-[0.625rem] text-destructive/70">need attention</p>
            </div>
          </Show>
        </section>

        <Show when={registryEntries().length > 0}>
          <section class="rounded-lg border border-border bg-card p-4 shadow-sm">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p class="text-xs font-semibold uppercase text-muted-foreground">
                  Adapter Registry
                </p>
                <p class="text-sm text-muted-foreground">
                  Live adapter registry entries.
                </p>
              </div>
              <div class="flex gap-3 text-xs text-muted-foreground">
                <span>{registryEntries().length} entries</span>
              </div>
            </div>
            <div class="mt-4 grid gap-2 sm:grid-cols-2">
              <For each={registryEntries()}>
                {(entry) => {
                  const providerMeta = getProvidersForDomain(entry.domain).find(
                    (provider) => provider.id === entry.provider,
                  );
                  const domainLabel =
                    DOMAIN_LABELS[entry.domain] ?? entry.domain;

                  return (
                    <div class="rounded-md border border-border bg-background px-4 py-3">
                      <div class="flex items-center justify-between gap-3">
                        <div>
                          <p class="text-sm font-semibold text-foreground">
                            {domainLabel}
                          </p>
                          <p class="text-xs text-muted-foreground">
                            {providerMeta?.name ?? entry.provider}
                          </p>
                        </div>
                        <AdapterStatusBadge status={entry.status} />
                      </div>
                    </div>
                  );
                }}
              </For>
            </div>
          </section>
        </Show>

        <section class="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setFilterStatus(undefined)}
            aria-pressed={!filterStatus()}
            class={cn(
              "rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none",
              !filterStatus()
                ? "border-primary bg-primary/10 text-primary"
                : "border-border bg-background text-muted-foreground hover:text-foreground",
            )}
          >
            All
          </button>
          <For each={[...ADAPTER_STATUSES]}>
            {(status) => (
              <button
                type="button"
                onClick={() => setFilterStatus(status)}
                aria-pressed={filterStatus() === status}
                class={cn(
                  "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none",
                  filterStatus() === status
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background text-muted-foreground hover:text-foreground",
                )}
              >
                <AdapterStatusBadge status={status} showLabel={false} />
                {status}
              </button>
            )}
          </For>
        </section>

        <AdapterList
          onSelect={(domain) => {
            if (props.onNavigateDetail) {
              props.onNavigateDetail(domain);
            } else {
              setSelectedDomain(domain);
            }
          }}
          filterStatus={filterStatus()}
        />

        <Show when={selectedDomain()}>
          {(domain) => (
            <div class="fixed inset-0 z-50 flex items-center justify-center bg-background/80 px-4 backdrop-blur-sm">
              <button
                type="button"
                aria-label="Close adapter configuration"
                class="absolute inset-0 cursor-default border-0 bg-transparent p-0"
                onClick={() => setSelectedDomain(null)}
              />
              <div
                ref={(element) => {
                  modalRef = element;
                }}
                class="relative z-10 w-full max-w-lg"
                role="dialog"
                aria-modal="true"
                aria-labelledby={modalTitleId}
                tabIndex={-1}
              >
                <AdapterConfigForm
                  domain={domain()}
                  titleId={modalTitleId}
                  initialConfig={initialConfigFor(domain())}
                  onSave={async (config) => {
                    await props.onConfigure?.(domain(), config);
                    setSelectedDomain(null);
                  }}
                  onCancel={() => setSelectedDomain(null)}
                />
              </div>
            </div>
          )}
        </Show>
      </div>
    </Show>
  );
}
